import React, { useState } from "react";
import {
  Box,
  Divider,
  Flex,
  Grid,
  HStack,
  Heading,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Text,
  Switch,
  Link,
  Tooltip,
} from "@chakra-ui/react";
import {
  Cake,
  ChevronLeft,
  ChevronRight,
  Heart,
  MapPin,
  Pencil,
  Send,
  Share2,
  Mail,
  Phone,
} from "lucide-react";

import type { ProfileSummary } from "src/types/matrimonyProfileDetails";
import ActionButton from "./ActionButton";
import KeyValueRow from "./KeyValueRow";
import { iconForPlatform } from "src/utils/socialMediaUtils";

export type ProfileHeaderCardProps = {
  data: ProfileSummary;
  /** Object URLs for additional gallery photos (already fetched via auth). */
  additionalPhotoUrls?: string[];
  onSendInterest?: () => void;
  onConnectNow: () => void;
  onLike: () => void;
  onShare: () => void;
  onEdit?: () => void;
  /** Optional: edit social links directly from the header when social icons are shown. */
  onEditSocialLinks?: () => void;
  activeToggle?: {
    value: boolean;
    isLoading?: boolean;
    onChange: (next: boolean) => void;
  };
  socialMediaLinksList?: Array<{ platform: string; url: string }> | null;
};

const ProfileHeaderCard: React.FC<ProfileHeaderCardProps> = ({
  data,
  additionalPhotoUrls = [],
  onSendInterest,
  onLike,
  onShare,
  onEdit,
  onEditSocialLinks,
  activeToggle,
  socialMediaLinksList,
}) => {
  // Build an array of all photos: primary first, then additional
  const allPhotos = [data.photoUrl, ...additionalPhotoUrls].filter(Boolean);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const activePhoto = allPhotos[selectedIdx] || data.photoUrl;
  const socialLinks = Array.isArray(socialMediaLinksList) ? socialMediaLinksList : [];

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const openLightbox = (idx: number) => {
    setLightboxIdx(idx);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);

  const lightboxPrev = () => setLightboxIdx((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
  const lightboxNext = () => setLightboxIdx((prev) => (prev + 1) % allPhotos.length);

  return (
    <Box
      bg="white"
      borderRadius="18px"
      boxShadow="0 10px 25px rgba(18, 18, 18, 0.06)"
      borderWidth="1px"
      borderColor="#EFE7E7"
      overflow="hidden"
      mt={{ base: 20, md: 20, xl: 20 }}
    >
      <Box p={{ base: 3, md: 5, xl: 6 }}>
        <Grid
          templateColumns={{ base: "1fr", md: "330px 1fr" }}
          gap={{ base: 3, md: 6, xl: 8 }}
          alignItems="start"
        >
          {/* Photo + thumbnails */}
          <Box position="relative" w="full" maxW={{ base: "240px", md: "330px" }} mx={{ base: "auto", md: 0 }} alignSelf="start" h="fit-content">
            <Box position="relative" cursor="pointer" borderRadius="18px" overflow="hidden" onClick={() => openLightbox(selectedIdx)}>
              <Image
                src={activePhoto}
                alt={data.photoAlt || `${data.name} profile photo`}
                objectFit="cover"
                w="full"
                h={{ base: "240px", md: "340px" }}
              />
              {/* Thumbnail overlay inside main image */}
              {allPhotos.length > 1 && (
                <HStack
                  position="absolute"
                  bottom={2}
                  left={2}
                  spacing={1.5}
                  onClick={(e) => e.stopPropagation()}
                >
                  {allPhotos.map((url, i) => (
                    <Box
                      key={i}
                      w={{ base: "36px", md: "44px" }}
                      h={{ base: "36px", md: "44px" }}
                      borderRadius="8px"
                      overflow="hidden"
                      cursor="pointer"
                      border={i === selectedIdx ? "2px solid white" : "2px solid rgba(255,255,255,0.5)"}
                      boxShadow={i === selectedIdx ? "0 0 0 1px #4A2C2A, 0 2px 8px rgba(0,0,0,0.3)" : "0 2px 6px rgba(0,0,0,0.25)"}
                      opacity={i === selectedIdx ? 1 : 0.85}
                      _hover={{ opacity: 1, border: "2px solid white" }}
                      transition="all 0.15s ease"
                      flexShrink={0}
                      onClick={() => setSelectedIdx(i)}
                    >
                      <Image src={url} alt={`Photo ${i + 1}`} w="100%" h="100%" objectFit="cover" />
                    </Box>
                  ))}
                </HStack>
              )}
            </Box>
          </Box>

          {/* Content */}
          <Flex direction="column" minW={0} pt={{ base: 2, md: 1 }} minH={{ md: "340px" }}>
            <Flex align={{ base: "center", md: "start" }} direction={{ base: "column", md: "row" }} gap={{ base: 2, md: 3 }}>
              <Box minW={0} textAlign={{ base: "center", md: "left" }} flex="1">
                <HStack
                  spacing={{ base: 2, md: 3 }}
                  justify={{ base: "center", md: "flex-start" }}
                  align="center"
                  flexWrap="wrap"
                >
                  <Heading size={{ base: "lg", md: "lg" }} color="#2b1b1a" noOfLines={2} fontWeight="900">
                    {data.name}
                  </Heading>

                  {activeToggle ? (
                    <HStack spacing={2} flexShrink={0}>
                      <Text fontWeight="600" color="#4A2C2A" fontSize="sm">
                        {activeToggle.value ? "Active" : "Inactive"}
                      </Text>
                      <Switch
                        isChecked={activeToggle.value}
                        onChange={(e) => activeToggle.onChange(e.target.checked)}
                        isDisabled={Boolean(activeToggle.isLoading)}
                        colorScheme="green"
                        aria-label="Active/Inactive profile"
                      />
                    </HStack>
                  ) : null}
                </HStack>

                <HStack
                  mt={2}
                  spacing={2}
                  flexWrap="wrap"
                  justify={{ base: "center", md: "flex-start" }}
                  color="#8a7b7b"
                  fontSize="sm"
                >
                  <Text as="span">Profile Managed by {data.managedBy}</Text>
                </HStack>
              </Box>

              <HStack
                spacing={2}
                pt={{ base: 2, md: 1 }}
                ml={{ base: 0, md: "auto" }}
                alignSelf={{ base: "center", md: "flex-start" }}
              >
                {onEdit ? (
                  <IconButton
                    aria-label="Edit profile"
                    icon={<Pencil size={18} />}
                    onClick={onEdit}
                    variant="outline"
                    borderRadius="12px"
                    borderColor="#E9DFDF"
                    color="#4A2C2A"
                    _hover={{ bg: "#FAF7F7" }}
                  />
                ) : null}
                <IconButton
                  aria-label="Like profile"
                  icon={<Heart size={18} />}
                  onClick={onLike}
                  variant="outline"
                  borderRadius="12px"
                  borderColor="#E9DFDF"
                  color="#4A2C2A"
                  _hover={{ bg: "#FAF7F7" }}
                />
                <IconButton
                  aria-label="Share profile"
                  icon={<Share2 size={18} />}
                  onClick={onShare}
                  variant="outline"
                  borderRadius="12px"
                  borderColor="#E9DFDF"
                  color="#4A2C2A"
                  _hover={{ bg: "#FAF7F7" }}
                />
              </HStack>
            </Flex>

            <Divider my={{ base: 3, md: 4, xl: 5 }} borderColor="#EFE7E7" />

            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 4, xl: 6 }}>
              <KeyValueRow label="Age & Height" value={`${data.stats.age} yrs, ${data.stats.height}`} icon={<Cake size={18} />} />
              <KeyValueRow label="Location" value={data.stats.location} icon={<MapPin size={18} />} />
              {data.email ? <KeyValueRow label="Email" value={data.email} icon={<Mail size={18} />} /> : null}
              {data.mobileNo ? <KeyValueRow label="Mobile" value={data.mobileNo} icon={<Phone size={18} />} /> : null}
              {/* Removed Job Role + Community to align with fields present in the Registration form */}
            </SimpleGrid>

            <Divider my={{ base: 3, md: 4, xl: 5 }} borderColor="#EFE7E7" />

             {socialLinks.length > 0 ? (
                <HStack
                  mt="auto"
                  pt={4}
                  spacing={2}
                  align="center"
                  justify={{ base: "flex-start", md: "flex-start" }}
                  flexWrap="wrap"
                  gridColumn={{ base: "1 / -1", md: "1 / -1" }}
                >
                  {socialLinks
                    .filter((x) => x?.platform && x?.url)
                    .map((x, idx) => (
                      <Tooltip key={`${x.platform}-${idx}`} label={x.platform}>
                        <Link href={x.url} isExternal>
                          <IconButton
                            aria-label={x.platform}
                            icon={iconForPlatform(x.platform)}
                            variant="outline"
                            borderRadius="12px"
                            borderColor="#E9DFDF"
                            color="#4A2C2A"
                            _hover={{ bg: "#FAF7F7" }}
                            size="sm"
                          />
                        </Link>
                      </Tooltip>
                    ))}

                  {onEditSocialLinks ? (
                    <Tooltip label="Edit social links">
                      <IconButton
                        aria-label="Edit social links"
                        icon={<Pencil size={18} />}
                        onClick={onEditSocialLinks}
                        variant="outline"
                        borderRadius="12px"
                        borderColor="#E9DFDF"
                        color="#4A2C2A"
                        _hover={{ bg: "#FAF7F7" }}
                        size="sm"
                      />
                    </Tooltip>
                  ) : null}
                </HStack>
              ) : null}

            {onSendInterest ? (
              <Flex mt={socialLinks.length > 0 ? 4 : "auto"} pt={socialLinks.length > 0 ? 0 : 4} justify={{ base: "stretch", md: "flex-end" }}>
                <Box w={{ base: "full", md: "auto" }}>
                  <ActionButton
                    variant="primary"
                    label="Send Interest"
                    ariaLabel="Send interest"
                    onClick={onSendInterest}
                    iconLeft={<Send size={18} />}
                  />
                </Box>
              </Flex>
            ) : null}
          </Flex>
        </Grid>
      </Box>

      {/* ── Lightbox Modal ──────────────────────────────────────── */}
      <Modal isOpen={lightboxOpen} onClose={closeLightbox} size="full" isCentered>
        <ModalOverlay bg="rgba(0,0,0,0.85)" />
        <ModalContent bg="transparent" boxShadow="none" maxW="100vw" maxH="100vh" m={0}>
          <ModalCloseButton
            color="white"
            size="lg"
            zIndex={2}
            top={4}
            right={4}
            _hover={{ bg: "rgba(255,255,255,0.15)" }}
          />
          <ModalBody
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={0}
            position="relative"
          >
            {/* Prev button */}
            {allPhotos.length > 1 && (
              <IconButton
                aria-label="Previous photo"
                icon={<ChevronLeft size={28} />}
                position="absolute"
                left={{ base: 2, md: 6 }}
                top="50%"
                transform="translateY(-50%)"
                borderRadius="full"
                bg="rgba(255,255,255,0.15)"
                color="white"
                _hover={{ bg: "rgba(255,255,255,0.3)" }}
                onClick={lightboxPrev}
                zIndex={2}
              />
            )}

            <Image
              src={allPhotos[lightboxIdx]}
              alt={`Photo ${lightboxIdx + 1}`}
              maxH="90vh"
              maxW="90vw"
              objectFit="contain"
              borderRadius="12px"
            />

            {/* Next button */}
            {allPhotos.length > 1 && (
              <IconButton
                aria-label="Next photo"
                icon={<ChevronRight size={28} />}
                position="absolute"
                right={{ base: 2, md: 6 }}
                top="50%"
                transform="translateY(-50%)"
                borderRadius="full"
                bg="rgba(255,255,255,0.15)"
                color="white"
                _hover={{ bg: "rgba(255,255,255,0.3)" }}
                onClick={lightboxNext}
                zIndex={2}
              />
            )}

            {/* Counter */}
            {allPhotos.length > 1 && (
              <Text
                position="absolute"
                bottom={4}
                left="50%"
                transform="translateX(-50%)"
                color="white"
                fontSize="sm"
                fontWeight="600"
                bg="rgba(0,0,0,0.5)"
                px={3}
                py={1}
                borderRadius="full"
              >
                {lightboxIdx + 1} / {allPhotos.length}
              </Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ProfileHeaderCard;
